import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Cloud, Server, Monitor, Loader2 } from "lucide-react";
import { EnvironmentVariables } from "./environment-variables";
import { ConfigurationFiles, UploadedFile } from "./configuration-files";

import { AppContextType } from "@/context/app";

interface DeploymentConfig {
  domain: string;
  region: string;
  size: string;
  environmentVars: { key: string; value: string }[];
  enableSSL: boolean;
  enableMonitoring: boolean;
  enableBackups: boolean;
}

const digitalOceanRegions = [
  { value: "nyc1", label: "New York 1" },
  { value: "nyc3", label: "New York 3" },
  { value: "ams3", label: "Amsterdam 3" },
  { value: "sgp1", label: "Singapore 1" },
  { value: "lon1", label: "London 1" },
  { value: "fra1", label: "Frankfurt 1" },
  { value: "tor1", label: "Toronto 1" },
  { value: "sfo3", label: "San Francisco 3" },
  { value: "blr1", label: "Bangalore 1" },
];

const dropletSizes = [
  {
    costPerMonth: "4",
    value: "s-1vcpu-512mb-10gb",
    label: "Basic - 1 vCPU, 512MB RAM ($4/mo)",
  },
  {
    costPerMonth: "6",
    value: "s-1vcpu-1gb",
    label: "Basic - 1 vCPU, 1GB RAM ($6/mo)",
  },
  {
    costPerMonth: "12",
    value: "s-1vcpu-2gb",
    label: "Basic - 1 vCPU, 2GB RAM ($12/mo)",
  },
  {
    costPerMonth: "18",
    value: "s-2vcpu-2gb",
    label: "Basic - 2 vCPU, 2GB RAM ($18/mo)",
  },
  {
    costPerMonth: "24",
    value: "s-2vcpu-4gb",
    label: "Basic - 2 vCPU, 4GB RAM ($24/mo)",
  },
  {
    costPerMonth: "48",
    value: "s-4vcpu-8gb",
    label: "Basic - 4 vCPU, 8GB RAM ($48/mo)",
  },
  {
    costPerMonth: "42",
    value: "c-2",
    label: "CPU-Optimized - 2 vCPU, 4GB RAM ($42/mo)",
  },
];

export function DeploymentSettings({
  project,
  onProvisionAndDeploy,
  isDeploying,
  deploymentFailed,
}: {
  project: { domainName?: string; apps?: AppContextType[] } | null;
  onProvisionAndDeploy: (config: Record<string, unknown>) => Promise<void>;
  isDeploying: boolean;
  deployMessage: string;
  deploymentProgress: number;
  deploymentFailed: boolean;
}) {
  const [config, setConfig] = useState<DeploymentConfig>(() => {
    const uniqueEnvs = new Set<string>();
    project?.apps?.forEach((app) => {
      app.requiredEnvs?.forEach((env) => uniqueEnvs.add(env));
    });

    return {
      domain: project?.domainName || "",
      region: "",
      size: "",
      environmentVars: Array.from(uniqueEnvs).map((key) => ({
        key,
        value: "",
      })),
      enableSSL: true,
      enableMonitoring: false,
      enableBackups: false,
    };
  });
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  useEffect(() => {
    if (project?.domainName) {
      updateConfig("domain", project.domainName);
    }
  }, [project?.domainName]);

  const updateConfig = (
    field: keyof DeploymentConfig,
    value: string | boolean | { key: string; value: string }[],
  ) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleDeploy = async () => {
    const payload = {
      domain: config.domain,
      domainName: config.domain,
      region: config.region,
      size: config.size,
      environmentVars: config.environmentVars.reduce(
        (agg, item) => ({ ...agg, [item.key]: item.value }),
        {},
      ),
      enableSSL: config.enableSSL,
      enableMonitoring: config.enableMonitoring,
      enableBackups: config.enableBackups,
      files: uploadedFiles,
    };
    await onProvisionAndDeploy(payload);
  };

  const isFormValid = () => {
    return config.region && config.size;
  };

  return (
    <div className="mx-auto space-y-8">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            Application Settings
          </CardTitle>
          <CardDescription>
            Configure your application deployment settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Region</Label>
            <Select
              value={config.region}
              onValueChange={(value) => updateConfig("region", value)}
              disabled={isDeploying}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a region" />
              </SelectTrigger>
              <SelectContent>
                {digitalOceanRegions.map((region) => (
                  <SelectItem key={region.value} value={region.value}>
                    {region.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Droplet Size</Label>
            <Select
              value={config.size}
              onValueChange={(value) => updateConfig("size", value)}
              disabled={isDeploying}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select droplet size" />
              </SelectTrigger>
              <SelectContent>
                {dropletSizes.map((size) => (
                  <SelectItem key={size.value} value={size.value}>
                    {size.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-4 border-t pt-4">
            <h4 className="mb-3 text-sm font-medium text-muted-foreground">
              Security Options
            </h4>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-sm">Auto SSL Certificate</Label>
                <p className="text-xs text-muted-foreground">
                  Automatically provision SSL certificates
                </p>
              </div>
              <Switch
                checked={config.enableSSL}
                onCheckedChange={(checked) =>
                  updateConfig("enableSSL", checked)
                }
                disabled={isDeploying}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="flex items-center gap-2 text-sm">
                  <Monitor className="h-3 w-3" />
                  Enable Monitoring
                </Label>
                <p className="text-xs text-muted-foreground">
                  Monitor droplet performance
                </p>
              </div>
              <Switch
                checked={config.enableMonitoring}
                onCheckedChange={(checked) =>
                  updateConfig("enableMonitoring", checked)
                }
                disabled={isDeploying}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-sm">Enable Backups</Label>
                <p className="text-xs text-muted-foreground">
                  Weekly automated backups (+20% cost)
                </p>
              </div>
              <Switch
                checked={config.enableBackups}
                onCheckedChange={(checked) =>
                  updateConfig("enableBackups", checked)
                }
                disabled={isDeploying}
              />
            </div>
          </div>

          <EnvironmentVariables
            environmentVars={config.environmentVars}
            onEnvironmentVarsChange={(newVars) =>
              updateConfig("environmentVars", newVars)
            }
            disabled={isDeploying}
            requiredKeys={(() => {
              const uniqueEnvs = new Set<string>();
              project?.apps?.forEach((app) => {
                app.requiredEnvs?.forEach((env) => uniqueEnvs.add(env));
              });
              return Array.from(uniqueEnvs);
            })()}
          />

          <ConfigurationFiles
            uploadedFiles={uploadedFiles}
            onUploadedFilesChange={setUploadedFiles}
            disabled={isDeploying}
          />
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <div></div>
        <Button
          onClick={handleDeploy}
          disabled={!isFormValid() || isDeploying}
          size="lg"
          className="min-w-[140px]"
        >
          {isDeploying ? (
            <>
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              Deploying...
            </>
          ) : deploymentFailed ? (
            "Retry Deploy"
          ) : (
            <>
              <Cloud className="mr-1 h-4 w-4" />
              Provision & Deploy
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
